import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
  Container,
  Stack,
  Grid,
  Breadcrumbs,
  Link,
  Tooltip,
  Fab,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Avatar,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Slide,
  Zoom,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Timer as TimerIcon,
  Assessment as AssessmentIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
  BookmarkBorder as BookmarkBorderIcon,
} from '@mui/icons-material';
import { JsonForms } from '@jsonforms/react';
import { materialRenderers, materialCells } from '@jsonforms/material-renderers';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import axiosClient from '../api/axios.client';

function FormRuntimePage({ darkMode: initialDarkMode }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Try to extract formId robustly
  const formIdFromParams = params.formId;
  let formIdFromQuery = null;
  if (location && location.search) {
    const query = new URLSearchParams(location.search);
    formIdFromQuery = query.get('formId');
  }
  // Only set initialFormId if a value is found (not empty string)
  const initialFormId = formIdFromParams || formIdFromQuery || undefined;

  // Use the darkMode prop from parent, with fallback to localStorage
  const [localDarkMode, setLocalDarkMode] = useState(() => {
    if (initialDarkMode !== undefined) return initialDarkMode;
    const saved = localStorage.getItem('theme');
    return saved !== 'light';
  });

  // Listen for theme changes from other components
  useEffect(() => {
    const handleThemeChange = (event) => {
      setLocalDarkMode(event.detail.darkMode);
    };

    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  // Update local state when prop changes
  useEffect(() => {
    if (initialDarkMode !== undefined) {
      setLocalDarkMode(initialDarkMode);
    }
  }, [initialDarkMode]);

  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showFormInfo, setShowFormInfo] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [formProgress, setFormProgress] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [startTime] = useState(Date.now());
  const [formId, setFormId] = useState(initialFormId);

  // Add state for search UI
  const [showSearch, setShowSearch] = useState(!formId);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    // Only update formId if a value is found (not empty string)
    // Since your route is /forms/form-runtime/:formId, formIdFromParams should always be present
    if (formIdFromParams) {
      setFormId(formIdFromParams);
    }
    // Do not set to empty string if both are falsy
  }, [formIdFromParams]);

  // Load form data
  useEffect(() => {
    // Only try to load if formId is defined and not empty string
    if (formId && formId !== '') {
      const loadForm = async () => {
        try {
          setLoading(true);

          let response;
          try {
            // Ensure this matches your backend route and axiosClient baseURL
            response = await axiosClient.get(`/forms/form-runtime/${formId}`);
          } catch (authError) {
            if (authError?.response?.status === 401 || authError?.response?.status === 403) {
              response = await axiosClient.get(`/forms/form-runtime/public/${formId}`);
            } else if (authError?.response?.status === 404) {
              setErrors(['Form not found or not published.']);
              setLoading(false);
              return;
            } else {
              throw authError;
            }
          }

          // Defensive: handle both {form: ...} and {success: true, form: ...}
          const formObj = response?.form || (response?.data && response.data.form);
          if (formObj) {
            // --- Always build schema/uischema from formElements if present ---
            let schema = formObj.schema || formObj.jsonSchema || {};
            let uischema = formObj.uischema || formObj.uiSchema || {};

            // If schema is missing or empty, but formElements exist, build schema/uischema dynamically
            if (
              (!schema || Object.keys(schema).length === 0)
              && Array.isArray(formObj.formElements)
              && formObj.formElements.length > 0
            ) {
              const { schema: builtSchema, uischema: builtUiSchema } = getSchemaAndUiSchema({
                formElements: formObj.formElements,
              });
              schema = builtSchema;
              uischema = builtUiSchema;
            }

            setForm({
              ...formObj,
              schema,
              uischema,
            });
            const initialData = {};
            if (formObj.formElements) {
              formObj.formElements.forEach((element) => {
                if (element.value !== undefined && element.value !== '') {
                  initialData[element.id] = element.value;
                }
              });
            }
            setFormData(initialData);
          } else {
            setErrors(['Form not found or not published.']);
          }
        } catch (error) {
          console.error('Failed to load form:', error);
          toast.error(error.message || 'Failed to load form');
          setErrors(['Failed to load form. Please check if the form exists and is published.']);
        } finally {
          setLoading(false);
        }
      };

      loadForm();
    }
  }, [formId]);

  // Time tracking effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeSpent(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  // Submit form data
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setValidationErrors([]);

      // Client-side validation
      if (!validateFormData()) {
        setSubmitting(false);
        return;
      }

      const startTime = Date.now();

      // Try authenticated submission first, fallback to public
      let response;
      try {
        response = await axiosClient.post(`/forms/form-runtime/${formId}/submit`, {
          data: formData,
          metadata: {
            processingTime: Date.now() - startTime,
            formVersion: form.version || '1.0.0',
            clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        });
      } catch (authError) {
        if (authError?.response?.status === 401 || authError?.response?.status === 403) {
          // Try public submission
          response = await axiosClient.post(`/forms/form-runtime/public/${formId}/submit`, {
            data: formData,
            metadata: {
              processingTime: Date.now() - startTime,
              formVersion: form.version || '1.0.0',
              clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          });
        } else {
          throw authError;
        }
      }

      if (response?.submissionId) {
        setSubmissionId(response.submissionId);
        setSubmitted(true);
        setCurrentStep(2);
        toast.success('Form submitted successfully!');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Failed to submit form');

      if (error.validationErrors) {
        setValidationErrors(error.validationErrors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Validate form data
  const validateFormData = () => {
    const newErrors = [];

    if (!form?.formElements) return true;

    form.formElements.forEach((element) => {
      if (element.required && (!formData[element.id] || formData[element.id] === '')) {
        newErrors.push(`${element.label} is required`);
      }

      if (element.type === 'email' && formData[element.id]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[element.id])) {
          newErrors.push(`${element.label} must be a valid email`);
        }
      }

      if (element.minLength && formData[element.id] && formData[element.id].length < element.minLength) {
        newErrors.push(`${element.label} must be at least ${element.minLength} characters`);
      }

      if (element.maxLength && formData[element.id] && formData[element.id].length > element.maxLength) {
        newErrors.push(`${element.label} must not exceed ${element.maxLength} characters`);
      }
    });

    if (newErrors.length > 0) {
      setValidationErrors(newErrors);
      toast.error('Please fix validation errors');
      return false;
    }

    return true;
  };

  // Save as draft
  const handleSaveDraft = async () => {
    try {
      await axiosClient.post(`/forms/form-runtime/${formId}/submit`, {
        data: formData,
        status: 'draft',
      });
      toast.success('Draft saved successfully!');
    } catch (error) {
      toast.error('Failed to save draft');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const steps = ['Fill Form', 'Review', 'Submit'];

  // Search forms API
  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const res = await axiosClient.get(`/forms/published?search=${encodeURIComponent(searchTerm)}`);
      setSearchResults(res.data?.forms || res.forms || []);
    } catch (e) {
      toast.error('Failed to search forms');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // When a form is selected from search, update formId and hide search UI
  useEffect(() => {
    if (formId && showSearch) {
      setShowSearch(false);
    }
  }, [formId, showSearch]);

  // If no formId, show search UI
  if (showSearch) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Search Published Forms
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <input
              type="text"
              placeholder="Enter form name or keyword"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: 4,
                border: '1px solid #ccc',
                fontSize: 16,
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={searchLoading || !searchTerm.trim()}
            >
              Search
            </Button>
          </Box>
          {searchLoading && <LinearProgress sx={{ mb: 2 }} />}
          {searchResults.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Select a form to fill:
              </Typography>
              <Stack spacing={2}>
                {searchResults.map((form) => (
                  <Card
                    key={form._id}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                    onClick={() => {
                      setFormId(form._id);
                      setShowSearch(false);
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6">{form.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {form.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
          {/* Only show this message if not loading, searchResults is empty, and a search has been performed */}
          {/* Remove or comment out the misleading message below */}
          {/*
          {!searchLoading && searchResults.length === 0 && searchTerm.trim() && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No forms found. Try searching with a different keyword.
            </Alert>
          )}
          */}
        </Paper>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
      >
        {/* Modern Loading Header */}
        <Paper
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Form Runtime
              </Typography>
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
                <Link
                  underline="hover"
                  color="inherit"
                  href="/"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <HomeIcon fontSize="small" />
                  Home
                </Link>
                <Typography color="text.primary">Forms</Typography>
                <Typography color="text.primary">Runtime</Typography>
              </Breadcrumbs>
            </Box>
          </Container>
        </Paper>

        {/* Loading Content */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        >
          <Card sx={{ p: 4, textAlign: 'center', minWidth: 300 }}>
            <CardContent>
              <LinearProgress sx={{ mb: 3 }} />
              <Typography variant="h6" color="primary" gutterBottom>
                Loading Form...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we prepare your form
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (errors.length > 0) {
    return (
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
      >
        {/* Error Header */}
        <Paper
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Form Runtime
              </Typography>
              <Chip label="Error" color="error" size="small" />
            </Box>
          </Container>
        </Paper>

        {/* Error Content */}
        <Container maxWidth="md" sx={{ flex: 1, py: 4 }}>
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <CardContent>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" color="error" gutterBottom>
                Form Load Error
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {errors[0]}
              </Alert>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  variant="outlined"
                  size="large"
                >
                  Go Back
                </Button>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => window.location.reload()}
                  variant="contained"
                  size="large"
                >
                  Retry
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  if (!form) {
    return (
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Form Runtime
              </Typography>
              <Chip label="Not Found" color="warning" size="small" />
            </Box>
          </Container>
        </Paper>

        {/* Not Found Content */}
        <Container maxWidth="md" sx={{ flex: 1, py: 4 }}>
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <CardContent>
              <WarningIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" color="warning.main" gutterBottom>
                Form Not Found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                The requested form could not be found or is not published.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  variant="outlined"
                  size="large"
                >
                  Go Back
                </Button>
                <Button
                  onClick={() => navigate('/forms/published')}
                  variant="contained"
                  size="large"
                >
                  Browse Forms
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
      >
        {/* Success Header */}
        <Paper
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Form Runtime
              </Typography>
              <Chip label="Submitted" color="success" size="small" />
            </Box>
          </Container>
        </Paper>

        {/* Success Content */}
        <Container
          maxWidth="md"
          sx={{
            flex: 1, display: 'flex', alignItems: 'center', py: 4,
          }}
        >
          <Card sx={{ width: '100%', textAlign: 'center' }}>
            <CardContent sx={{ p: 6 }}>
              <CheckCircleIcon sx={{ fontSize: 96, color: 'success.main', mb: 3 }} />
              <Typography variant="h3" gutterBottom color="success.main" sx={{ fontWeight: 600 }}>
                Form Submitted Successfully!
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                Thank you for your submission. Your response has been recorded and will be processed accordingly.
              </Typography>

              {/* Submission Details */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3, mb: 4, bgcolor: 'grey.50', maxWidth: 400, mx: 'auto',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Submission Details
                </Typography>
                {submissionId && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>ID:</strong>
                    {' '}
                    {submissionId}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Date:</strong>
                  {' '}
                  {new Date().toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong>
                  {' '}
                  {new Date().toLocaleTimeString()}
                </Typography>
              </Paper>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  size="large"
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => window.location.reload()}
                  size="large"
                  sx={{ fontWeight: 600 }}
                >
                  Submit Another
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // Add this handler before the return statement
  const handleGoToReview = () => {
    setCurrentStep(1);
  };

  // Helper to render review summary as a read-only list
  const renderReviewSummary = () => {
    if (!form?.formElements) return null;
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="h6">Review your answers below before submitting.</Typography>
        </Alert>
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Stack spacing={2}>
            {form.formElements.map((el) => {
              // Defensive: handle undefined el.options and nested fields
              let value = formData[el.id];

              // Defensive: ensure el is defined and is an object
              if (!el || typeof el !== 'object') return null;

              // Defensive: ensure el.options is an array if present
              if (Array.isArray(el.options)) {
                value = el.options.includes(value) ? value : (value ?? '');
              }

              // Handle grid fields (array of objects)
              if (el.type === 'grid' && Array.isArray(value)) {
                return (
                  <Box key={el.id} sx={{ mb: 2 }}>
                    <Typography sx={{ minWidth: 180, fontWeight: 500, mb: 1 }}>
                      {el.label}
                      :
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      {value.length === 0 ? (
                        <Typography color="text.secondary"><em>No rows</em></Typography>
                      ) : (
                        value.map((row, idx) => (
                          <Box key={idx} sx={{ mb: 1, pl: 1 }}>
                            {(Array.isArray(el.columns) ? el.columns : []).map((col) => (
                              <Box key={col?.key || col?.id || idx} sx={{ display: 'flex', gap: 1 }}>
                                <Typography sx={{ minWidth: 120 }}>
                                  {col?.label || col?.key || col?.id}
                                  :
                                </Typography>
                                <Typography color="text.secondary">
                                  {row && col && row[col.key] !== undefined && row[col.key] !== ''
                                    ? String(row[col.key])
                                    : <em>Not filled</em>}
                                </Typography>
                              </Box>
                            ))}
                            <Divider sx={{ my: 1 }} />
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                );
              }

              // Skip groupbox (handled by its fields) and grid (handled above)
              if (el.type === 'groupbox' || el.type === 'grid') return null;

              return (
                <Box key={el.id} sx={{ display: 'flex', gap: 2 }}>
                  <Typography sx={{ minWidth: 180, fontWeight: 500 }}>
                    {el.label}
                    :
                  </Typography>
                  <Typography color="text.secondary">
                    {value !== undefined && value !== ''
                      ? String(value)
                      : <em>Not filled</em>}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      flex: 1,
      height: '100vh',
    }}
    >
      {/* Enhanced Header */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        {/* Top Navigation Bar */}
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          >
            {/* Left Section - Title and Breadcrumbs */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {form.name || 'Form Runtime'}
              </Typography>
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
                <Link
                  underline="hover"
                  color="inherit"
                  href="/"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <HomeIcon fontSize="small" />
                  Home
                </Link>
                <Typography color="text.primary">Forms</Typography>
                <Typography color="text.primary">Runtime</Typography>
              </Breadcrumbs>
            </Box>
            {/* Right Section - Actions and Info */}
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Form Progress */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<AssessmentIcon fontSize="small" />}
                  label={`${Math.round(formProgress)}% Complete`}
                  size="small"
                  color={formProgress > 80 ? 'success' : formProgress > 50 ? 'warning' : 'primary'}
                  variant="outlined"
                />
                <Chip
                  icon={<TimerIcon fontSize="small" />}
                  label={`${Math.floor(timeSpent / 60)}:${(timeSpent % 60).toString().padStart(2, '0')}`}
                  size="small"
                  variant="outlined"
                />
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              {/* Action Buttons */}
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                variant="outlined"
                size="small"
              >
                Back
              </Button>
            </Stack>
          </Box>
        </Container>
      </Paper>

      {/* Main Content with right vertical panel for Stepper */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'row', minHeight: 0,
      }}
      >
        {/* Main Form Content */}
        <Container
          maxWidth="lg"
          sx={{
            flex: 1,
            py: { xs: 2, sm: 4 },
            px: { xs: 2, sm: 3 },
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Please fix the following errors:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Form Card */}
          <Card
            elevation={2}
            sx={{
              mb: 3, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
            }}
          >
            <CardContent
              sx={{
                p: { xs: 3, sm: 4 },
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden', // <-- Prevent CardContent from scrolling
              }}
            >
              {/* Form Header */}
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  {form.name}
                </Typography>
                {form.description && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {form.description}
                  </Typography>
                )}

                {/* Progress Bar */}
                <Box sx={{ mt: 3 }}>
                  <LinearProgress
                    variant="determinate"
                    value={formProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {Math.round(formProgress)}
                    % Complete
                  </Typography>
                </Box>
              </Box>

              {/* Form Content */}
              <Box sx={{
                '& .MuiTextField-root': { mb: 3 },
                '& .MuiFormControl-root': { mb: 3 },
                '& .MuiFormGroup-root': { mb: 3 },
                flex: 1,
                minHeight: 0,
                maxHeight: '100%', // Use flexbox context, not a fixed px value
                overflow: 'auto',
              }}
              >
                {form.schema && form.uischema && Object.keys(form.schema.properties || {}).length > 0 ? (
                  <>
                    <JsonForms
                      schema={form.schema}
                      uischema={form.uischema}
                      data={formData}
                      renderers={materialRenderers}
                      cells={materialCells}
                      onChange={({ data }) => {
                        setFormData(data);
                        // Calculate progress
                        const requiredFields = form.formElements?.filter((el) => el.required) || [];
                        const filledRequiredFields = requiredFields.filter((el) => data[el.id] !== undefined && data[el.id] !== null && data[el.id] !== '');
                        const progress = requiredFields.length > 0
                          ? (filledRequiredFields.length / requiredFields.length) * 100
                          : 100;
                        setFormProgress(progress);
                      }}
                    />
                    {/* Show Review button only on Fill Form step */}
                    {currentStep === 0 && (
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleGoToReview}
                          disabled={formProgress < 100}
                          startIcon={<NavigateNextIcon />}
                        >
                          Review
                        </Button>
                      </Box>
                    )}
                    {/* Show review summary if on Review step */}
                    {currentStep === 1 && renderReviewSummary()}
                  </>
                ) : (
                  // ...existing error/debug info...
                  <Alert severity="error" sx={{ mb: 2 }}>
                    This form cannot be rendered because its schema or uischema is missing or empty.
                    Please contact the form creator to publish the form with a valid schema.
                  </Alert>
                )}
              </Box>
            </CardContent>

            {/* Action Footer */}
            <Box sx={{
              p: 3,
              pt: 0,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
            >
              {/* Left Actions */}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveDraft}
                  disabled={autoSaving}
                  size="large"
                >
                  {autoSaving ? 'Saving...' : 'Save Draft'}
                </Button>
              </Stack>

              {/* Right Actions */}
              <Stack direction="row" spacing={2}>
                {/* Show Submit button only on Review step */}
                {currentStep === 1 ? (
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleSubmit}
                    disabled={submitting}
                    size="large"
                    sx={{
                      fontWeight: 600,
                      minWidth: 140,
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Form'}
                  </Button>
                ) : null}
              </Stack>
            </Box>
          </Card>
        </Container>

        {/* Vertical Stepper Panel on the right */}
        <Box
          sx={{
            width: { xs: 100, sm: 140, md: 180 }, // Increased width for better visibility
            bgcolor: 'background.paper',
            borderLeft: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            minHeight: 0,
            position: 'relative',
          }}
        >
          <Stepper
            activeStep={currentStep}
            orientation="vertical"
            sx={{
              height: '100%',
              '& .MuiStep-root': { mb: 3 },
              alignItems: 'center',
            }}
          >
            {['Fill Form', 'Review', 'Submit'].map((label, idx) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: { fontSize: 32, mb: 1 },
                  }}
                  sx={{
                    '.MuiStepLabel-label': {
                      fontSize: { xs: '1rem', sm: '1.15rem', md: '1.2rem' }, // Slightly larger font
                      fontWeight: currentStep === idx ? 700 : 400,
                      color: currentStep === idx ? 'primary.main' : 'text.secondary',
                      textAlign: 'center',
                      whiteSpace: 'normal',
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>
    </Box>
  );
}

// Add this utility function at the top (or import from your FormRuntime.jsx)
function getSchemaAndUiSchema({ formElements }) {
  // Minimal version for runtime use (copy your logic from FormRuntime.jsx)
  const getJsonSchemaType = (widgetType) => {
    switch (widgetType) {
      case 'number':
        return 'number';
      case 'checkbox':
        return 'boolean';
      case 'date':
      case 'datetime':
      case 'text':
      case 'textarea':
      case 'listbox':
      case 'combobox':
      case 'radiobuttons':
        return 'string';
      default:
        return 'string';
    }
  };
  const properties = (formElements || []).reduce((acc, el) => {
    if (el.type === 'grid') {
      acc[el.id] = {
        type: 'array',
        title: el.label,
        items: {
          type: 'object',
          properties: Array.isArray(el.columns)
            ? el.columns.reduce((cols, col) => {
              cols[col.key || col.id || `col_${Math.random()}`] = {
                type: getJsonSchemaType(col.type),
                title: col.label || col.key || col.type,
              };
              return cols;
            }, {})
            : {},
        },
      };
      return acc;
    }
    if (el.type === 'groupbox') {
      return acc;
    }
    const type = getJsonSchemaType(el.type);
    const elementSchema = { type, title: el.label };
    if (el.type === 'date') elementSchema.format = 'date';
    if (el.type === 'datetime') elementSchema.format = 'date-time';
    if (el.options && el.options.length) elementSchema.enum = el.options;
    if (el.minLength) elementSchema.minLength = el.minLength;
    if (el.maxLength) elementSchema.maxLength = el.maxLength;
    if (el.pattern) elementSchema.pattern = el.pattern;
    if (el.minimum) elementSchema.minimum = el.minimum;
    if (el.maximum) elementSchema.maximum = el.maximum;
    if (el.multipleOf) elementSchema.multipleOf = el.multipleOf;
    acc[el.key || el.id] = elementSchema;
    return acc;
  }, {});
  const runtimeSchema = { type: 'object', properties };
  const runtimeUiSchema = {
    type: 'VerticalLayout',
    elements: (formElements || []).map((el) => {
      if (el.type === 'grid') {
        return {
          type: 'Control',
          scope: `#/properties/${el.id}`,
          options: { table: true },
        };
      }
      return {
        type: 'Control',
        scope: `#/properties/${el.key || el.id}`,
      };
    }),
  };
  return { schema: runtimeSchema, uischema: runtimeUiSchema };
}

export default FormRuntimePage;
